import {
  ICreateInvoicePayload,
  IInvoiceRecord,
  EQuantityUnit,
  EMonetaryAmountBy,
  ECurrency,
  EPaymentMethod,
  TPaymentMethodCreate,
  EEmailPermissionLevel,
  IEmailRecipient,
  IPaymentManualEntryCreate,
} from "../interfaces/invoice.interfaces";
import { EmailRecipient } from "../EmailRecipients/types";

const BANK_TRANSFER_LOCATION_KEY = "default";
const getBankTransferStorageKey = (locationKey: string) => `bankTransfer.details.${locationKey}`;

type StoredBankTransfer = Partial<{
  accountHolderName: string;
  accountNumber: string | number;
  routingNumber: string | number;
}>;

const readBankTransferFromStorage = (locationKey = BANK_TRANSFER_LOCATION_KEY): StoredBankTransfer | null => {
  try {
    const raw = localStorage.getItem(getBankTransferStorageKey(locationKey));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredBankTransfer | null;
    if (!parsed || typeof parsed !== "object") return null;

    return parsed;
  } catch {
    return null;
  }
};

const createInvoiceRecord = (cost: number, currency: ECurrency = ECurrency.USD, alias?: string): IInvoiceRecord => ({
  quantity: {
    value: 1,
    unit: EQuantityUnit.PER_ITEM,
  },
  price: {
    value: cost,
    by: EMonetaryAmountBy.UNIQUE,
    currency,
  },
  total: {
    value: cost,
    by: EMonetaryAmountBy.UNIQUE,
    currency,
  },
  ...(alias ? { alias } : {}),
});

interface PaymentMethodData {
  selectedPayments: string[];
  referenceNumber: string;
  markAsPaid: boolean;
  manualEntryNotes?: string;
  manualEntryTransactionId?: string;
}

interface GenericInvoiceMapperInput {
  identifierId?: string;
  lineItems: Array<{ id?: string; title: string; amount: number; description?: string }>;
  paymentMethod: PaymentMethodData;
  emailRecipients: EmailRecipient[];
  notes: string | null;
  sendEmail: boolean;
  feeAmount?: number;
  serviceFees?: Array<{
    alias: string;
    kind: "amount" | "percentage";
    value: number;
    amount: number;
    appliesTo?: { type: "invoice" | "service" | "extra_line_item"; extraLineItemId?: string; label?: string };
  }>;
  currency?: ECurrency;
  externalUid?: string;
  companyId?: number;
  phoneNumber?: string;
}

export const mapGenericInvoiceToBackendPayload = ({
  identifierId,
  lineItems,
  paymentMethod,
  emailRecipients,
  notes,
  sendEmail = true,
  feeAmount = 0,
  serviceFees,
  currency = ECurrency.USD,
  externalUid,
  companyId,
  phoneNumber,
}: GenericInvoiceMapperInput): ICreateInvoicePayload => {
  
  // Calculate subtotal from extra line items
  const subtotal = Math.round(lineItems.reduce((acc, item) => acc + item.amount, 0) * 100) / 100;
  
  // Grand total is subtotal + fee
  const total = Math.round((subtotal + feeAmount) * 100) / 100;

  // Map payment methods
  const paymentMethods: TPaymentMethodCreate[] = [];

  paymentMethod.selectedPayments.forEach((paymentId) => {
    const id = paymentId.toLowerCase();

    if (id === "paypal") {
      paymentMethods.push({ provider: EPaymentMethod.PAYPAL });
      return;
    }

    if (id === "stripe") {
      paymentMethods.push({ provider: EPaymentMethod.STRIPE });
      return;
    }

    if (id === "bank_transfer") {
      const stored = readBankTransferFromStorage();
      const accountHolderName = String(stored?.accountHolderName ?? "").trim() || "Bank Transfer";
      const routingNumberRaw = stored?.routingNumber ?? paymentMethod.referenceNumber ?? "";
      const routingNumber = String(routingNumberRaw).trim() || "N/A";
      const accountNumberRaw = stored?.accountNumber ?? "";
      const accountNumber = String(accountNumberRaw).trim() || undefined;

      paymentMethods.push({
        provider: EPaymentMethod.BANK_TRANSFER,
        bankTransferInfo: {
          routingNumber,
          accountHolderName,
          ...(accountNumber ? { accountNumber } : {}),
        }
      });
      return;
    }
  });

  // If markAsPaid is true, add MANUAL_ENTRY as a payment method
  if (paymentMethod.markAsPaid) {
    const manualEntry: IPaymentManualEntryCreate = {
      provider: EPaymentMethod.MANUAL_ENTRY,
    };
    
    if (paymentMethod.manualEntryNotes) {
      manualEntry.notes = paymentMethod.manualEntryNotes;
    }
    
    if (paymentMethod.manualEntryTransactionId) {
      manualEntry.transactionId = paymentMethod.manualEntryTransactionId;
    }
    
    paymentMethods.push(manualEntry);
  }

  // Map email recipients
  const mappedEmailRecipients: IEmailRecipient[] = emailRecipients.map((recipient) => ({
    email: recipient.email,
    permissionLevel: recipient.permissionLevel === "Can Pay" 
      ? EEmailPermissionLevel.CAN_PAY 
      : EEmailPermissionLevel.CAN_VIEW,
    isLocked: recipient.isLocked,
  }));

  // Build pricingDetail to preserve granular line item detail
  const pricingDetail = {
    serviceLine: {
      qty: 1,
      unitPrice: 0,
      lineTotal: 0,
      isLineTotalOverridden: false,
    },
    isGlobalTotalOverridden: false,
    extraLineItems: lineItems.map((item) => ({
      ...(item.id ? { id: item.id } : {}),
      title: item.title.trim(),
      amount: item.amount,
      ...(item.description ? { description: item.description } : {}),
    })),
    serviceFees:
      serviceFees && serviceFees.length > 0
        ? serviceFees.map((fee) => ({
            ...createInvoiceRecord(fee.amount, currency, fee.alias),
            kind: fee.kind,
            value: fee.value,
            appliesTo: fee.appliesTo ?? { type: "invoice" as const },
          }))
        : feeAmount > 0
        ? [
            {
              ...createInvoiceRecord(feeAmount, currency),
              kind: "amount" as const,
              value: feeAmount,
              appliesTo: { type: "invoice" as const },
            },
          ]
        : [],
    ...(externalUid && { externalUid }),
    ...(companyId && { companyId }),
    ...(phoneNumber && { phoneNumber }),
  };

  return {
    identifierId: identifierId || undefined,
    pricingDetail,
    ...(feeAmount > 0 && {
      increasePercentageByFrequency: createInvoiceRecord(feeAmount, currency),
    }),
    totalCost: createInvoiceRecord(total, currency),
    paymentMethods,
    emailRecipients: mappedEmailRecipients.length > 0 ? mappedEmailRecipients : undefined,
    notes: notes || null,
    sendEmail: sendEmail !== false,
  };
};
